import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';

import { NotFoundState } from '@/shared/components/NotFoundState';

import { BuilderPage, builderSearchSchema } from '@/features/workflows';

export const Route = createFileRoute(
  '/workspaces/$workspaceId/workflows/$workflowKey/builder',
)({
  validateSearch: builderSearchSchema,
  // Deliberately no prefetching loader, matching the board route next door and
  // for a sharper version of its reason: the builder holds unsaved edits, and a
  // loader that refetches the aggregate on every arrival would hand the page a
  // new document object. The page renders its own pending state, so a loader
  // buys nothing but a way to lose work.
  component: BuilderRoute,
  notFoundComponent: WorkflowNotFound,
});

export function BuilderRoute() {
  const { workspaceId, workflowKey } = Route.useParams();
  const { sel } = Route.useSearch();
  const navigate = useNavigate();

  return (
    <BuilderPage
      workspaceId={workspaceId}
      workflowKey={workflowKey}
      selection={sel}
      onSelectionChange={(next) => {
        // Replace rather than push: selecting a node is not a navigation, and
        // filling the history with every click would make Back mean "undo my
        // last selection" instead of "leave the builder".
        void navigate({
          to: '.',
          search: { sel: next },
          replace: true,
        });
      }}
    />
  );
}

export function WorkflowNotFound() {
  const { workspaceId } = Route.useParams();
  return (
    <NotFoundState
      title="Workflow not found"
      description="This workspace has no workflow with that key. It may have been renamed or removed since the link was shared."
      // The list is where you pick a workflow that does exist — the same
      // reasoning the board page already applies when a key resolves to
      // nothing mid-session.
      action={
        <Link
          to="/workspaces/$workspaceId/workflows"
          params={{ workspaceId }}
          className="text-primary focus-visible:ring-ring rounded-sm text-sm hover:underline focus-visible:ring-3 focus-visible:outline-none"
        >
          Go to all workflows
        </Link>
      }
    />
  );
}
